import { FormRow, InputContainer } from '@creditornot/cb-form';
import { ModalHeader } from '@creditornot/cb-modal';
import { Select } from '@creditornot/cb-select';
import { useMutation } from '@tanstack/react-query';
import React, { useContext, useMemo } from 'react';
import { Field, Form } from 'react-final-form';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';

import { bulkCreateWoltSpecials } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { BulkCreateWoltSpecialsPayload, Category, MenuContainer } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { RFFFieldErrorWrapper } from 'common/components';
import { CurrencyContext } from 'common/formatting/CurrencyContext';
import validateYupSchema from 'common/utils/validateYupSchema';
import { reportBulkAction } from 'modules/menu/behavioral';
import { useBulkCreateWoltSpecialsTracking } from 'modules/menu/hooks/telemetryHooks';
import useMenuData from 'modules/menu/useMenuData';

import AvailabilityInputs from './AvailabilityInputs';
import DetailedErrorList from './DetailedErrorList';
import DiscountInputs from './DiscountInputs';
import { CancelButton, Content, Footer, Root, SubmitButton } from './styled';
import { fromFormValuesToBatchCreatePayload, getUserRequirementOptions, isErrorResponseWithDetails } from './utils';
import { bulkCreateSpecialsValidationSchema } from './validation';
import BulkActionModalTitle from '../BulkActionModalTitle';

type Props = {
  categories: Category[];
  menuId: string;
  onClearItemSelection: () => void;
  onClose: () => void;
  selectedItemIds: Set<string>;
};

export type BulkCreateSpecialsFormValues = {
  discount: number | string | undefined;
  discountType: 'absolute' | 'relative-percentage' | 'none';
  availabilityStart: number | undefined;
  availabilityEnd: number | undefined;
  userRequirement: string | undefined;
};

const initialFormValues: BulkCreateSpecialsFormValues = {
  discount: undefined,
  discountType: 'none',
  availabilityStart: undefined,
  availabilityEnd: undefined,
  userRequirement: undefined,
};

const BulkCreateSpecialsModal = ({ categories, menuId, onClose, onClearItemSelection, selectedItemIds }: Props) => {
  const intl = useIntl();
  const { updateMenuData } = useMenuData();
  const { currency } = useContext(CurrencyContext);

  const userRequirementOptions = useMemo(() => {
    return getUserRequirementOptions(intl);
  }, [intl]);

  const trackWoltSpecialsCreation = useBulkCreateWoltSpecialsTracking([...selectedItemIds]);

  const handleCreateSpecials = useMutation(
    (payload: BulkCreateWoltSpecialsPayload) => bulkCreateWoltSpecials(menuId, payload),
    {
      onMutate: () => {
        reportBulkAction(selectedItemIds.size, 'BULK_CREATE_WOLT_SPECIALS');
      },
      onSuccess: (data: MenuContainer, variables: BulkCreateWoltSpecialsPayload) => {
        updateMenuData({ actionType: 'updateMenuContainer', payload: data });
        onClearItemSelection();
        toast.success(
          <FormattedMessage
            id="menu.bulkActions.createWoltSpecials.successDescription"
            values={{ count: selectedItemIds.size }}
          />,
        );
        trackWoltSpecialsCreation({
          discountAmount: Math.round(Number(variables.discount)),
          firstTimeCustomers: variables.user_purchase_requirement ?? '',
          startDateSelected: variables.availability_period?.start?.$date ?? 0,
          endDateSelected: variables.availability_period?.end?.$date ?? 0,
        });
      },
      onError: error => {
        if (isErrorResponseWithDetails(error) && error.response) {
          const errorCount = Object.keys(error.response.data.error.items_errors).length;
          const successCount = selectedItemIds.size - errorCount;

          if (successCount > 0) {
            toast.success(
              <FormattedMessage
                id="menu.bulkActions.createWoltSpecials.successDescription"
                values={{ count: successCount }}
              />,
            );
          }

          if (errorCount > 0) {
            toast.error(<DetailedErrorList itemErrorsMap={error.response.data.error.items_errors} />, {
              duration: Infinity,
            });
          }

          onClearItemSelection();
          updateMenuData({ actionType: 'updateMenuContainer', payload: error.response.data.modified_menu_container });
        } else {
          toast.error(
            <FormattedMessage
              id="menu.bulkActions.createWoltSpecials.errorDescription"
              values={{ count: selectedItemIds.size }}
            />,
            { duration: Infinity },
          );
        }
      },
      onSettled: () => {
        onClose();
      },
    },
  );

  return (
    <Root isOpen>
      <ModalHeader onCloseClick={onClose}>
        <BulkActionModalTitle
          count={selectedItemIds.size}
          title={<FormattedMessage id="menu.bulkActions.createWoltSpecials.header" />}
        />
      </ModalHeader>
      <Form<BulkCreateSpecialsFormValues>
        initialValues={initialFormValues}
        onSubmit={values => {
          const validValues = bulkCreateSpecialsValidationSchema.validateSync(values);
          const payload = fromFormValuesToBatchCreatePayload(categories, validValues, selectedItemIds);

          handleCreateSpecials.mutate(payload);
        }}
        validate={validateYupSchema(bulkCreateSpecialsValidationSchema, {
          currency,
        })}
      >
        {({ handleSubmit, hasValidationErrors }) => (
          <form onSubmit={handleSubmit}>
            <Content>
              <FormRow label={intl.formatMessage({ id: 'menu-item.discount' })} horizontalPadding>
                <Field<BulkCreateSpecialsFormValues['discount']> name="discount">
                  {discountField => (
                    <RFFFieldErrorWrapper field={discountField} showErrorImmediately={false}>
                      {discountInput => (
                        <Field<BulkCreateSpecialsFormValues['discountType']> name="discountType">
                          {({ input: discountTypeInput }) => (
                            <DiscountInputs
                              currency={currency}
                              discountInput={discountInput}
                              discountTypeInput={discountTypeInput}
                              selectedItemIds={selectedItemIds}
                            />
                          )}
                        </Field>
                      )}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
              </FormRow>
              <FormRow
                label={intl.formatMessage({ id: 'special.availability-picker-starting-label' })}
                horizontalPadding
              >
                <Field<BulkCreateSpecialsFormValues['availabilityStart']> name="availabilityStart">
                  {field => (
                    <RFFFieldErrorWrapper field={field} showErrorImmediately>
                      {input => <AvailabilityInputs {...input} />}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
              </FormRow>
              <FormRow label={intl.formatMessage({ id: 'special.availability-picker-ending-label' })} horizontalPadding>
                <Field<BulkCreateSpecialsFormValues['availabilityEnd']> name="availabilityEnd">
                  {field => (
                    <RFFFieldErrorWrapper field={field} showErrorImmediately>
                      {input => <AvailabilityInputs {...input} />}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
              </FormRow>
              <FormRow label={intl.formatMessage({ id: 'menu-item.ftu-restriction-label' })} horizontalPadding>
                <Field<BulkCreateSpecialsFormValues['userRequirement']> name="userRequirement">
                  {field => (
                    <InputContainer>
                      <Select {...field.input} clearable options={userRequirementOptions} menuPlacement="top" />
                    </InputContainer>
                  )}
                </Field>
              </FormRow>
            </Content>
            <Footer>
              <CancelButton onClick={onClose}>
                <FormattedMessage id="state.cancel" />
              </CancelButton>
              <SubmitButton
                type="submit"
                onClick={handleSubmit}
                disabled={handleCreateSpecials.isLoading || hasValidationErrors}
                loading={handleCreateSpecials.isLoading}
              >
                <FormattedMessage id="state.save" />
              </SubmitButton>
            </Footer>
          </form>
        )}
      </Form>
    </Root>
  );
};

export default BulkCreateSpecialsModal;
